import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchProcessedBankStatements,
  fetchProcessedCCStatements,
  fetchCaseCheckImages,
  fetchCaseGlEntries,
  fetchProcessedExpenseReports,
} from '../api/cases';

export type SourceType = 'bank_statement' | 'credit_card' | 'check_image' | 'general_ledger' | 'expense_report';

export interface UnifiedRow {
  /** React key - unique within a single load, may have a source prefix. */
  id: string;
  /**
   * The real, stable, persistent record id from the source data
   * (transaction_id / entry_id / check_image_id) - the same ids already
   * used as acct_entity_mentions.record_id. Custom-column classification
   * results are cached keyed off this, NOT off `id`, so it must be the
   * durable source-of-record id, never a synthetic/positional one.
   */
  record_id: string;
  date: string;
  description: string;
  amount: number | null;
  source_type: SourceType;
  account_number: string;
  account_last4: string;
  institution: string;
  transaction_type: string;
  file_name: string;
  file_id: string;
  // Source-specific extras - expected sparse across a mixed-source table.
  debit_amount?: number;
  credit_amount?: number;
  gl_account_code?: string;
  gl_account_name?: string;
  check_number?: string;
  payee?: string;
  statement_date?: string;
}

export interface TransactionFilters {
  sourceTypes: Set<SourceType>;
  accounts: Set<string>; // matches against account_number or gl_account_name
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string;   // YYYY-MM-DD
}

const ALL_SOURCE_TYPES: SourceType[] = ['bank_statement', 'credit_card', 'check_image', 'general_ledger', 'expense_report'];

export const defaultFilters = (): TransactionFilters => ({
  sourceTypes: new Set(ALL_SOURCE_TYPES),
  accounts: new Set(),
});

function last4(accountNumber: string | undefined): string {
  const digits = String(accountNumber || '').replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : digits;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const cleaned = typeof value === 'string' ? value.replace(/[$,]/g, '') : value;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

// Check-image dates are extracted from handwritten/printed checks and are
// inconsistently formatted (MM/DD/YYYY, or sometimes missing the year
// entirely, e.g. "09/30") - normalize what's parseable to ISO, leave the
// rest as-is rather than dropping the row.
function normalizeLooseDate(value: string | undefined): string {
  if (!value) return '';
  // Check-image dates come from OCR on handwritten/printed checks and show
  // up in whatever format the check happened to use - MM/DD/YYYY,
  // MM/DD/YY, or even dot-separated (8.30.24). Normalize what's parseable
  // to ISO; leave anything else as-is rather than dropping the row.
  const parts = value.split(/[/.]/);
  if (parts.length === 3) {
    const [mm, dd, yearRaw] = parts;
    if (/^\d{1,2}$/.test(mm) && /^\d{1,2}$/.test(dd) && /^\d{2,4}$/.test(yearRaw)) {
      // Forensic/litigation records routinely include much older checks -
      // "check images don't predate 2000" was a bad assumption (a real
      // check dated 5/13/94 was getting mapped to 2094). Use the standard
      // windowing convention instead: 00-49 -> 20xx, 50-99 -> 19xx.
      const yyyy = yearRaw.length === 2
        ? (parseInt(yearRaw, 10) <= 49 ? `20${yearRaw}` : `19${yearRaw}`)
        : yearRaw;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
  }
  return value;
}

// GL_PAGE_LIMIT caps how many GL rows a single fetch pulls back - GL ledgers
// can run into the tens of thousands of entries for one case (far more than
// bank/CC statements ever have), so unlike the other three sources this one
// is always fetched filtered/paginated server-side, never "everything".
const GL_PAGE_LIMIT = 5000;

function glEntryToRow(e: any): UnifiedRow {
  return {
    id: `gl-${e.entry_id}`,
    record_id: e.entry_id ?? '',
    date: normalizeGlDate(e.entry_date),
    description: e.description ?? e.memo ?? '',
    amount: toNumber(e.net_amount),
    source_type: 'general_ledger',
    account_number: '',
    account_last4: '',
    institution: e.company_name ?? '',
    transaction_type: e.transaction_type ?? '',
    file_name: e.original_filename ?? e.filename ?? e.file_id ?? '',
    file_id: e.file_id ?? '',
    debit_amount: toNumber(e.debit_amount) ?? undefined,
    credit_amount: toNumber(e.credit_amount) ?? undefined,
    gl_account_code: e.account_code ?? '',
    gl_account_name: e.account_name ?? '',
  };
}

function accountKeyFor(row: UnifiedRow): string {
  return row.source_type === 'general_ledger'
    ? `gl|${row.gl_account_code}|${row.gl_account_name}`
    : `${row.institution}|${row.account_number}`;
}

function matchesFilters(row: UnifiedRow, filters: TransactionFilters): boolean {
  if (filters.dateFrom && row.date && row.date < filters.dateFrom) return false;
  if (filters.dateTo && row.date && row.date > filters.dateTo) return false;
  if (filters.accounts.size > 0 && !filters.accounts.has(accountKeyFor(row))) return false;
  return true;
}

export interface ExportProgress {
  loaded: number;
  total: number;
}

interface UseCaseTransactionsResult {
  rows: UnifiedRow[];
  loading: boolean;
  error: string | null;
  availableAccounts: { key: string; label: string }[];
  glTruncated: boolean;
  glTotalMatched: number;
  refresh: () => void;
  /**
   * Fetches the COMPLETE filtered row set for export, independent of the
   * capped preview. Bank/CC/check are already fully loaded so this reuses
   * them; GL is paginated through in full (a case can have tens of
   * thousands of entries - far more than the preview ever holds), reporting
   * progress via onProgress as pages come in.
   */
  fetchAllRowsForExport: (onProgress?: (p: ExportProgress) => void) => Promise<UnifiedRow[]>;
}

export function useCaseTransactions(
  caseId: string,
  token: string | undefined,
  filters: TransactionFilters,
): UseCaseTransactionsResult {
  const [bankRows, setBankRows] = useState<UnifiedRow[]>([]);
  const [ccRows, setCcRows] = useState<UnifiedRow[]>([]);
  const [checkRows, setCheckRows] = useState<UnifiedRow[]>([]);
  const [glRows, setGlRows] = useState<UnifiedRow[]>([]);
  const [expenseRows, setExpenseRows] = useState<UnifiedRow[]>([]);
  const [glMeta, setGlMeta] = useState<{ total_matched: number; has_more: boolean; accounts: { account_code: string; account_name: string }[] }>({
    total_matched: 0,
    has_more: false,
    accounts: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // ── Bank / CC / check images: small per case, fetched in full once ──────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const [bankResp, ccResp, checkResp, expenseResp] = await Promise.allSettled([
          fetchProcessedBankStatements(caseId, token),
          fetchProcessedCCStatements(caseId, token),
          fetchCaseCheckImages(caseId, token),
          fetchProcessedExpenseReports(caseId, token),
        ]);

        if (cancelled) return;

        if (bankResp.status === 'fulfilled') {
          const rows: UnifiedRow[] = [];
          for (const stmt of bankResp.value.data?.statements ?? []) {
            for (const tx of stmt.transactions ?? []) {
              rows.push({
                id: `bank-${tx.transaction_id || `${stmt.statement_id}-${rows.length}`}`,
                record_id: tx.transaction_id ?? '',
                date: tx.date ?? '',
                description: tx.description ?? '',
                amount: toNumber(tx.amount),
                source_type: 'bank_statement',
                account_number: stmt.account_number ?? '',
                account_last4: last4(stmt.account_number),
                institution: stmt.bank_name ?? '',
                transaction_type: tx.transaction_type ?? '',
                file_name: stmt.filename ?? stmt.file_id,
                file_id: stmt.file_id,
                statement_date: stmt.statement_date ?? '',
              });
            }
          }
          setBankRows(rows);
        }

        if (ccResp.status === 'fulfilled') {
          const rows: UnifiedRow[] = [];
          for (const stmt of ccResp.value.data?.statements ?? []) {
            for (const tx of stmt.transactions ?? []) {
              rows.push({
                id: `cc-${tx.transaction_id || `${stmt.statement_id}-${rows.length}`}`,
                record_id: tx.transaction_id ?? '',
                date: tx.date ?? '',
                description: tx.description ?? '',
                amount: toNumber(tx.amount),
                source_type: 'credit_card',
                account_number: stmt.account_number ?? '',
                account_last4: last4(stmt.account_number),
                institution: stmt.card_issuer ?? '',
                transaction_type: tx.transaction_type ?? '',
                file_name: stmt.filename ?? stmt.file_id,
                file_id: stmt.file_id,
                statement_date: stmt.statement_date ?? '',
              });
            }
          }
          setCcRows(rows);
        }

        if (checkResp.status === 'fulfilled') {
          const rows: UnifiedRow[] = [];
          for (const check of checkResp.value.data?.check_images ?? []) {
            rows.push({
              id: `check-${check.check_image_id ?? rows.length}`,
              record_id: check.check_image_id ?? '',
              date: normalizeLooseDate(check.date),
              description: `Check — ${check.payee ?? 'Unknown payee'}`,
              amount: toNumber(check.amount),
              source_type: 'check_image',
              account_number: check.account_number ?? '',
              account_last4: last4(check.account_number),
              institution: check.bank_name ?? '',
              transaction_type: 'check',
              file_name: check.filename ?? check.file_id ?? '',
              file_id: check.file_id ?? '',
              check_number: check.check_number ?? '',
              payee: check.payee ?? '',
            });
          }
          setCheckRows(rows);
        }

        if (expenseResp.status === 'fulfilled') {
          const rows: UnifiedRow[] = [];
          for (const entry of expenseResp.value.data?.entries ?? []) {
            rows.push({
              id: `expense-${entry.entry_id ?? rows.length}`,
              record_id: entry.entry_id ?? '',
              date: entry.entry_date ?? '',
              description: entry.description ?? '',
              amount: toNumber(entry.amount),
              source_type: 'expense_report',
              account_number: '',
              account_last4: '',
              institution: entry.entity_name ?? '',
              transaction_type: 'expense',
              file_name: entry.filename ?? entry.file_id ?? '',
              file_id: entry.file_id ?? '',
              payee: entry.paid_to ?? '',
            });
          }
          setExpenseRows(rows);
        }

        if (bankResp.status === 'rejected' && ccResp.status === 'rejected' && checkResp.status === 'rejected') {
          setError('Could not load transaction data for this case.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [caseId, token, refreshCounter]);

  // ── GL entries: filtered/paginated server-side, refetched when the date
  //    range or account filter changes (only when GL is an active source) ────
  useEffect(() => {
    if (!filters.sourceTypes.has('general_ledger')) {
      setGlRows([]);
      setGlMeta(prev => ({ ...prev, total_matched: 0, has_more: false }));
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const resp = await fetchCaseGlEntries(caseId, {
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
          limit: GL_PAGE_LIMIT,
        }, token);
        if (cancelled) return;

        const body = resp.data;
        const rows: UnifiedRow[] = (body.entries ?? []).map(glEntryToRow);

        setGlRows(rows);
        setGlMeta({
          total_matched: body.total_matched ?? rows.length,
          has_more: !!body.has_more,
          accounts: body.accounts ?? [],
        });
      } catch {
        if (!cancelled) setError(prev => prev ?? 'Could not load general ledger entries for this case.');
      }
    })();

    return () => { cancelled = true; };
  }, [caseId, token, filters.sourceTypes.has('general_ledger'), filters.dateFrom, filters.dateTo, refreshCounter]);

  // Scoped to the currently active source types, so unchecking a source
  // (e.g. Bank Statements) removes its accounts from the list entirely
  // rather than leaving stale, no-longer-relevant options checked.
  const availableAccounts = useMemo(() => {
    const map = new Map<string, string>();
    const sourceRows = [
      ...(filters.sourceTypes.has('bank_statement') ? bankRows : []),
      ...(filters.sourceTypes.has('credit_card') ? ccRows : []),
      ...(filters.sourceTypes.has('check_image') ? checkRows : []),
    ];
    for (const row of sourceRows) {
      if (!row.account_number) continue;
      const key = `${row.institution}|${row.account_number}`;
      map.set(key, `${row.institution || 'Unknown'} (...${row.account_last4 || row.account_number})`);
    }
    if (filters.sourceTypes.has('general_ledger')) {
      for (const acc of glMeta.accounts) {
        const key = `gl|${acc.account_code}|${acc.account_name}`;
        map.set(key, `GL: ${acc.account_name}`);
      }
    }
    return Array.from(map.entries()).map(([key, label]) => ({ key, label }));
  }, [bankRows, ccRows, checkRows, glMeta.accounts, filters.sourceTypes]);

  const rows = useMemo(() => {
    const combined = [
      ...(filters.sourceTypes.has('bank_statement') ? bankRows : []),
      ...(filters.sourceTypes.has('credit_card') ? ccRows : []),
      ...(filters.sourceTypes.has('check_image') ? checkRows : []),
      ...(filters.sourceTypes.has('general_ledger') ? glRows : []),
      ...(filters.sourceTypes.has('expense_report') ? expenseRows : []),
    ];

    return combined
      .filter(row => matchesFilters(row, filters))
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [bankRows, ccRows, checkRows, glRows, expenseRows, filters]);

  const refresh = useCallback(() => setRefreshCounter(c => c + 1), []);

  const fetchAllRowsForExport = useCallback(async (
    onProgress?: (p: ExportProgress) => void,
  ): Promise<UnifiedRow[]> => {
    const nonGlRows = [
      ...(filters.sourceTypes.has('bank_statement') ? bankRows : []),
      ...(filters.sourceTypes.has('credit_card') ? ccRows : []),
      ...(filters.sourceTypes.has('check_image') ? checkRows : []),
      ...(filters.sourceTypes.has('expense_report') ? expenseRows : []),
    ].filter(row => matchesFilters(row, filters));

    if (!filters.sourceTypes.has('general_ledger')) {
      onProgress?.({ loaded: nonGlRows.length, total: nonGlRows.length });
      return nonGlRows.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    }

    // Page through every matching GL entry - date filtering happens
    // server-side (cheap, indexed by case), account filtering happens here
    // client-side per page since the endpoint only supports one substring
    // match, and our account selection can span multiple GL accounts.
    const glAll: UnifiedRow[] = [];
    let offset = 0;
    let total = Infinity;
    while (offset < total) {
      const resp = await fetchCaseGlEntries(caseId, {
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        limit: GL_PAGE_LIMIT,
        offset,
      }, token);
      const body = resp.data;
      total = body.total_matched ?? 0;
      const pageRows: UnifiedRow[] = (body.entries ?? []).map(glEntryToRow);
      for (const row of pageRows) {
        if (matchesFilters(row, filters)) glAll.push(row);
      }
      offset += (body.entries ?? []).length;
      onProgress?.({ loaded: Math.min(offset, total), total });
      if (!body.has_more || (body.entries ?? []).length === 0) break;
    }

    return [...nonGlRows, ...glAll].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [caseId, token, filters, bankRows, ccRows, checkRows, expenseRows]);

  return {
    rows,
    loading,
    error,
    availableAccounts,
    glTruncated: filters.sourceTypes.has('general_ledger') && glMeta.has_more,
    glTotalMatched: glMeta.total_matched,
    refresh,
    fetchAllRowsForExport,
  };
}

function normalizeGlDate(mmddyyyy: string | undefined): string {
  if (!mmddyyyy) return '';
  const parts = mmddyyyy.split('/');
  if (parts.length !== 3) return mmddyyyy;
  const [mm, dd, yyyy] = parts;
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
}
